import React, { Suspense } from 'react';
// Views
import ErrorFallback from '../views/ErrorFallback';
import DetailLayout from '../views/DetailLayout';

import Page from '../components/templates/Page/Page';
import {
  normalizeDetailPage,
  normalizePagePath,
} from '../domain/detail/detailRouteUseCase';

/**
 * Dynamic page components mapping based on page type
 * Uses React.lazy() for code splitting and dynamic imports
 * @type {Record<'table' | 'detail' | 'timeline' | 'dashboard', React.LazyExoticComponent<any>>}
 */
const pageComponents = {
  table: React.lazy(() => import('../views/Table')),
  detail: React.lazy(() => import('../views/Detail')),
  timeline: React.lazy(() => import('../views/Timeline')),
  dashboard: React.lazy(() => import('../views/Dashboard')),
};

/**
 * @param {ApiPage} page
 * @returns {boolean}
 */
const isDetailTemplatePage = (page) => (
  page.template === 'detail' || page.template === 'dashboard'
);

/**
 * Child route marker — URL matching only; DetailLayout renders the UI.
 * @returns {null}
 */
function DetailRouteMatch() {
  return null;
}

/**
 * @param {{
 *   page: ApiPage
 * }} props
 * @returns {React.ReactElement}
 */
export default function DynamicPage({ page }) {
  /** @type {React.ComponentType<{page: ApiPage}>} */
  const Component = pageComponents[page.template];

  if (!Component) {
    return <ErrorFallback />;
  }

  return (
    <Page page={page}>
      <Suspense fallback={<div />}>
        <Component key={page.path} page={page} />
      </Suspense>
    </Page>
  );
}

/**
 * @param {ApiPage} page
 * @returns {React.ReactElement}
 */
const createElement = (page) => <DynamicPage page={page} />;

/**
 * Creates dynamic routes array from JSON configuration with React Query integration
 * @param {Array<ApiPage>} pages - Array of page configurations from JSON
 * @returns {RouteObject[]} Array of React Router route objects
 */
export const createDynamicRoutes = (
  pages,
) => {
  if (!Array.isArray(pages)) {
    throw new Error('Pages parameter must be an array');
  }

  const detailPages = pages.filter(isDetailTemplatePage);
  const otherPages = pages.filter((page) => !isDetailTemplatePage(page));

  const otherRoutes = otherPages.map((page, index) => {
    const pagePath = page.path.split(/[?#]/)[0];

    if (!pageComponents[page.template]) {
      // eslint-disable-next-line no-console
      console.error(`Unknown page type "${page.template}" in page ${index}. Available types: ${Object.keys(pageComponents).join(', ')}`);
      return {
        path: pagePath,
        element: <ErrorFallback />,
        errorElement: <ErrorFallback />,
        id: `${page.path}`,
      };
    }

    return {
      id: `${page.path}`,
      path: `/${pagePath}`,
      element: createElement(page),
      errorElement: <ErrorFallback />,
    };
  });

  const normalizedDetailPages = detailPages.map(normalizeDetailPage);

  const detailLayoutRoute = normalizedDetailPages.length > 0 ? [{
    id: 'detail-layout',
    element: (
      <Suspense fallback={<div />}>
        <DetailLayout pages={normalizedDetailPages} />
      </Suspense>
    ),
    errorElement: <ErrorFallback />,
    children: normalizedDetailPages.map((page) => ({
      id: page.path,
      path: normalizePagePath(page.path),
      element: <DetailRouteMatch />,
    })),
  }] : [];

  return [
    ...otherRoutes,
    ...detailLayoutRoute,
  ];
};

export {
  pageComponents,
  createElement,
};
